// Motor de la grilla tipo Excel (STOCK → Artículos en modo planilla — Martin,
// 6 oct 2026, sobre el prototipo aprobado). Imperativo a propósito: con miles
// de celdas, redibujar el DOM a mano (y solo las filas a la vista) es lo que
// mantiene fluido moverse con las flechas. La pantalla (ArticulosPlanillaPage)
// trae los datos, los filtros del servidor y guarda; acá vive la edición.

const FILA = 24          // alto de cada fila (px) — fijo: lo usa el dibujado por ventana
const CAB = 44           // alto del encabezado (letras 20 + nombres 24)
const RN = 42            // ancho de la columna de números de fila
const COLCHON = 12       // filas de más arriba y abajo de lo visible

export const LETRA = i => { let s = ''; i++; while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26) } return s }
const esc = s => String(s).replace(/[&<>"]/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]))
const rango = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i)

/**
 * cols: [{ k, l, w, ro?, ed?: 'texto'|'bool', si?, no?, mono?, num?, fz?, fmt?(v) }]
 * on: { filtro(j, rect), cambio(resumen), aviso(texto), filtroActivo(j) → bool }
 */
export function crearPlanilla(hoja, cols, on) {
  const tabla = document.createElement('table')
  tabla.className = 'pl-tabla'
  hoja.appendChild(tabla)

  let filas = [], orig = [], vista = []
  let cur = { r: 0, c: 0 }, anc = { r: 0, c: 0 }
  let editando = null, orden = null
  const pila = []
  let arrastrando = false, rellenando = null, raf = 0

  const texto = (c, v) => {
    if (c.ed === 'bool') return v ? c.si : c.no
    if (c.fmt) return c.fmt(v)
    return v == null ? '' : String(v)
  }
  const parseBool = (c, s) => {
    const t = String(s).trim().toLowerCase()
    if (['si', 'sí', 's', '1', 'true', c.si.toLowerCase()].includes(t)) return true
    if (['no', 'n', '0', 'false', c.no.toLowerCase()].includes(t)) return false
    return null
  }
  const esDirty = (i, k) => filas[i][k] !== orig[i][k]
  const sel = () => ({ r1: Math.min(cur.r, anc.r), r2: Math.max(cur.r, anc.r), c1: Math.min(cur.c, anc.c), c2: Math.max(cur.c, anc.c) })

  function recalcVista() {
    vista = filas.map((_, i) => i)
    if (orden) {
      const c = cols.find(x => x.k === orden.k)
      vista.sort((a, b) => {
        const x = filas[a][c.k], y = filas[b][c.k]
        const r = c.num ? (Number(x ?? -Infinity) - Number(y ?? -Infinity)) : texto(c, x).localeCompare(texto(c, y), 'es', { numeric: true })
        return orden.dir * r
      })
    }
    const max = Math.max(vista.length - 1, 0)
    cur.r = Math.min(cur.r, max); anc.r = Math.min(anc.r, max)
  }

  function render() {
    let left = RN
    const lefts = {}
    cols.forEach(c => { if (c.fz) { lefts[c.k] = left; left += c.w } })
    const g = sel()
    const alto = hoja.clientHeight || 600
    const desde = Math.max(0, Math.floor((hoja.scrollTop - CAB) / FILA) - COLCHON)
    const hasta = Math.min(vista.length - 1, Math.ceil((hoja.scrollTop + alto) / FILA) + COLCHON)
    const st = c => (c.fz ? `left:${lefts[c.k]}px;` : '')
    let h = `<colgroup><col style="width:${RN}px">` + cols.map(c => `<col style="width:${c.w}px">`).join('') + '</colgroup>'
    h += `<thead><tr class="pl-letras"><th class="pl-rn"></th>` + cols.map((c, j) => `<th class="${c.fz ? 'pl-fz' : ''}" style="${st(c)}">${LETRA(j)}</th>`).join('') + '</tr>'
    h += `<tr class="pl-nombres"><th class="pl-rn">#</th>` + cols.map((c, j) => `<th class="${c.ro ? 'pl-ro' : 'pl-ed'} ${c.num ? 'pl-num' : ''} ${c.fz ? 'pl-fz' : ''}" style="${st(c)}" title="${c.ro ? esc(c.nota || 'Viene de Presea') + ': no se edita acá' : 'Campo de LGR: se edita acá'}"><div class="pl-h"><span>${esc(c.l)}</span><button class="pl-fbtn ${on.filtroActivo?.(j) || orden?.k === c.k ? 'on' : ''}" data-f="${j}" type="button" aria-label="Filtrar ${esc(c.l)}">▾</button></div><div class="pl-resize" data-rs="${j}"></div></th>`).join('') + '</tr></thead><tbody>'
    if (desde > 0) h += `<tr class="pl-esp" style="height:${desde * FILA}px"><td colspan="${cols.length + 1}"></td></tr>`
    for (let r = desde; r <= hasta; r++) {
      const i = vista[r], fila = filas[i]
      h += `<tr><td class="pl-rn">${r + 1}</td>`
      cols.forEach((c, j) => {
        const v = fila[c.k]
        const cls = [c.ro ? 'pl-ro' : '', c.mono ? 'pl-mono' : '', c.num ? 'pl-num' : '', c.fz ? 'pl-fz' : '', esDirty(i, c.k) ? 'pl-dirty' : '',
          r >= g.r1 && r <= g.r2 && j >= g.c1 && j <= g.c2 ? 'pl-sel' : '', r === cur.r && j === cur.c ? 'pl-cur' : '',
          c.ed === 'texto' && !v ? 'pl-vacio' : ''].filter(Boolean).join(' ')
        let cont = c.ed === 'bool' ? `<span class="pl-chip ${v ? 'si' : 'no'}">${esc(texto(c, v))}</span>` : (c.ed === 'texto' && !v ? '—' : esc(texto(c, v)))
        if (r === g.r2 && j === g.c2 && !editando) cont += '<div class="pl-handle" data-handle="1" title="Arrastrá para copiar hacia abajo"></div>'
        h += `<td data-r="${r}" data-c="${j}" class="${cls}" style="${st(c)}" title="${esc(texto(c, v))}">${cont}</td>`
      })
      h += '</tr>'
    }
    if (hasta < vista.length - 1) h += `<tr class="pl-esp" style="height:${(vista.length - 1 - hasta) * FILA}px"><td colspan="${cols.length + 1}"></td></tr>`
    h += '</tbody>'
    tabla.innerHTML = h
    on.cambio?.(resumen())
  }

  function resumen() {
    let celdas = 0
    const prods = new Set()
    filas.forEach((f, i) => cols.forEach(c => { if (esDirty(i, c.k)) { celdas++; prods.add(i) } }))
    const g = sel()
    let n = 0, suma = 0, hayNum = false
    for (let r = g.r1; r <= g.r2; r++) for (let j = g.c1; j <= g.c2; j++) {
      const v = filas[vista[r]]?.[cols[j].k]
      if (v !== '' && v != null) n++
      if (cols[j].num && v != null && v !== '' && !isNaN(Number(v))) { suma += Number(v); hayNum = true }
    }
    const c = cols[cur.c], i = vista[cur.r]
    return {
      celdas, prods: prods.size, deshacer: pila.length, filas: vista.length,
      ref: LETRA(cur.c) + (cur.r + 1), valor: i == null ? '' : texto(c, filas[i][c.k]), ro: !!c.ro, nota: c.nota || null,
      seleccion: g.r2 > g.r1 || g.c2 > g.c1 ? { n, suma: hayNum ? suma : null } : null,
    }
  }

  // ── Edición ──
  function setCeldas(cambios) {
    const prev = cambios.filter(x => filas[x.i][x.k] !== x.v).map(x => ({ i: x.i, k: x.k, v: filas[x.i][x.k] }))
    if (!prev.length) return 0
    pila.push(prev)
    cambios.forEach(x => { filas[x.i][x.k] = x.v })
    return prev.length
  }
  function empezar(inicial) {
    const c = cols[cur.c]
    if (!vista.length) return
    if (c.ro) { on.aviso?.(c.nota ? `«${c.l}»: ${c.nota.toLowerCase()}, no se edita acá` : `«${c.l}» viene de Presea: se corrige en Presea, no acá`); return }
    if (c.ed === 'bool') { setCeldas([{ i: vista[cur.r], k: c.k, v: !filas[vista[cur.r]][c.k] }]); render(); return }
    editando = { r: cur.r, c: cur.c }
    render()
    const td = tabla.querySelector(`td[data-r="${cur.r}"][data-c="${cur.c}"]`)
    if (!td) { editando = null; return }
    const lista = [...new Set(filas.map(f => f[c.k]).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), 'es'))
    const dl = 'pl-dl-' + c.k
    td.innerHTML = `<input class="pl-input" id="pl-input" list="${dl}" value="${esc(inicial ?? filas[vista[cur.r]][c.k] ?? '')}"><datalist id="${dl}">${lista.map(v => `<option value="${esc(v)}">`).join('')}</datalist>`
    const inp = td.querySelector('input')
    inp.focus({ preventScroll: true })
    if (inicial == null) inp.select(); else inp.setSelectionRange(inp.value.length, inp.value.length)
    inp.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); terminar(true); mover(e.key === 'Tab' ? 0 : (e.shiftKey ? -1 : 1), e.key === 'Tab' ? (e.shiftKey ? -1 : 1) : 0, false) }
      else if (e.key === 'Escape') { e.preventDefault(); terminar(false) }
      e.stopPropagation()
    })
    inp.addEventListener('blur', () => { if (editando) terminar(true) })
  }
  function terminar(guardar) {
    if (!editando) return
    const inp = tabla.querySelector('#pl-input'), c = cols[editando.c], r = editando.r
    editando = null
    if (guardar && inp) setCeldas([{ i: vista[r], k: c.k, v: inp.value.trim() }])
    render(); hoja.focus({ preventScroll: true })
  }
  function borrarSel() {
    const g = sel(), cambios = []
    let ro = 0
    for (let r = g.r1; r <= g.r2; r++) for (let j = g.c1; j <= g.c2; j++) { const c = cols[j]; if (c.ro) { ro++; continue } if (c.ed === 'texto') cambios.push({ i: vista[r], k: c.k, v: '' }) }
    setCeldas(cambios); render()
    if (ro) on.aviso?.('Las columnas de Presea no se borran desde acá')
  }
  function rellenar(desde, hasta, cs) {
    const cambios = []
    for (const j of cs) { const c = cols[j]; if (c.ro) continue; const v = filas[vista[desde]][c.k]; for (let r = desde + 1; r <= hasta; r++) cambios.push({ i: vista[r], k: c.k, v }) }
    const n = setCeldas(cambios); render()
    if (n) on.aviso?.(`${n} ${n === 1 ? 'celda rellenada' : 'celdas rellenadas'}`)
  }
  function undo() { const p = pila.pop(); if (!p) return; p.forEach(x => { filas[x.i][x.k] = x.v }); render() }

  // ── Navegación ──
  function asegurarVisible() {
    const top = CAB + cur.r * FILA
    if (top + FILA > hoja.scrollTop + hoja.clientHeight) hoja.scrollTop = top + FILA - hoja.clientHeight + 2
    if (top - CAB < hoja.scrollTop) hoja.scrollTop = Math.max(0, top - CAB)
    if (!cols[cur.c].fz) {
      const fz = cols.filter(c => c.fz).reduce((s, c) => s + c.w, RN)
      let x = RN
      for (let j = 0; j < cur.c; j++) x += cols[j].w
      const w = cols[cur.c].w
      if (x + w > hoja.scrollLeft + hoja.clientWidth) hoja.scrollLeft = x + w - hoja.clientWidth + 2
      if (x - fz < hoja.scrollLeft) hoja.scrollLeft = Math.max(0, x - fz)
    }
  }
  function mover(dr, dc, extender) {
    if (!vista.length) return
    cur = { r: Math.max(0, Math.min(vista.length - 1, cur.r + dr)), c: Math.max(0, Math.min(cols.length - 1, cur.c + dc)) }
    if (!extender) anc = { ...cur }
    asegurarVisible(); render()
  }
  function copiar() {
    const g = sel(), out = []
    for (let r = g.r1; r <= g.r2; r++) out.push(rango(g.c1, g.c2).map(j => texto(cols[j], filas[vista[r]][cols[j].k]).replace(/\t|\n/g, ' ')).join('\t'))
    const n = (g.r2 - g.r1 + 1) * (g.c2 - g.c1 + 1)
    navigator.clipboard?.writeText(out.join('\n')).then(() => on.aviso?.(`${n} ${n === 1 ? 'celda copiada' : 'celdas copiadas'}`), () => on.aviso?.('El navegador no dejó copiar'))
  }

  const onKey = e => {
    if (editando) return
    const mod = e.metaKey || e.ctrlKey, k = e.key
    if (k.startsWith('Arrow')) { e.preventDefault(); const [dr, dc] = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }[k]; mover(mod ? dr * 1e6 : dr, mod ? dc * 999 : dc, e.shiftKey); return }
    if (k === 'Tab') { e.preventDefault(); mover(0, e.shiftKey ? -1 : 1, false); return }
    if (k === 'Enter') { e.preventDefault(); mover(e.shiftKey ? -1 : 1, 0, false); return }
    if (k === 'F2') { e.preventDefault(); empezar(); return }
    if (k === ' ' && cols[cur.c].ed === 'bool') { e.preventDefault(); empezar(); return }
    if (k === 'Delete' || k === 'Backspace') { e.preventDefault(); borrarSel(); return }
    if (mod && k.toLowerCase() === 'c') { e.preventDefault(); copiar(); return }
    if (mod && k.toLowerCase() === 'z') { e.preventDefault(); undo(); return }
    if (mod && k.toLowerCase() === 'd') { e.preventDefault(); const g = sel(); rellenar(g.r1, g.r2, rango(g.c1, g.c2)); return }
    if (mod && k.toLowerCase() === 'a') { e.preventDefault(); anc = { r: 0, c: 0 }; cur = { r: Math.max(vista.length - 1, 0), c: cols.length - 1 }; render(); return }
    if (!mod && k.length === 1) {
      const c = cols[cur.c]
      e.preventDefault()
      if (c.ed === 'bool') { const b = parseBool(c, k); if (b !== null) { setCeldas([{ i: vista[cur.r], k: c.k, v: b }]); render() } return }
      empezar(k)
    }
  }
  const onPaste = e => {
    if (editando || !hoja.contains(document.activeElement)) return
    const txt = e.clipboardData?.getData('text/plain')
    if (!txt || !vista.length) return
    e.preventDefault()
    const bloque = txt.replace(/\r/g, '').replace(/\n$/, '').split('\n').map(l => l.split('\t'))
    const g = sel()
    // Un solo valor pegado sobre un rango: se repite en todo el rango (como Excel).
    const unico = bloque.length === 1 && bloque[0].length === 1
    const alto = unico ? g.r2 - g.r1 + 1 : bloque.length, ancho = unico ? g.c2 - g.c1 + 1 : bloque[0].length
    const cambios = []
    let ro = 0, malos = 0
    for (let a = 0; a < alto; a++) for (let b = 0; b < ancho; b++) {
      const r = g.r1 + a, j = g.c1 + b
      if (r >= vista.length || j >= cols.length) continue
      const c = cols[j], v = unico ? bloque[0][0] : (bloque[a]?.[b] ?? '')
      if (c.ro) { ro++; continue }
      if (c.ed === 'bool') { const bv = parseBool(c, v); if (bv === null) { malos++; continue } cambios.push({ i: vista[r], k: c.k, v: bv }) } else cambios.push({ i: vista[r], k: c.k, v: v.trim() })
    }
    const n = setCeldas(cambios)
    anc = { r: g.r1, c: g.c1 }
    cur = { r: Math.min(g.r1 + alto - 1, vista.length - 1), c: Math.min(g.c1 + ancho - 1, cols.length - 1) }
    render()
    on.aviso?.(`${n} ${n === 1 ? 'celda pegada' : 'celdas pegadas'}` + (ro ? ` · ${ro} en columnas de Presea, no se tocaron` : '') + (malos ? ` · ${malos} valores que no son Sí/No` : ''))
  }
  const onDown = e => {
    if (e.target.closest('.pl-fbtn') || e.target.closest('input')) return
    const rs = e.target.dataset?.rs
    if (rs != null) {
      e.preventDefault()
      const c = cols[+rs], x0 = e.clientX, w0 = c.w
      const mv = ev => { c.w = Math.max(48, w0 + ev.clientX - x0); tabla.querySelectorAll('col')[+rs + 1].style.width = c.w + 'px' }
      const up = () => { document.removeEventListener('mousemove', mv); document.removeEventListener('mouseup', up); render() }
      document.addEventListener('mousemove', mv); document.addEventListener('mouseup', up)
      return
    }
    const td = e.target.closest('td[data-r]')
    if (!td) return
    if (editando) terminar(true)
    if (e.target.dataset.handle) { rellenando = { desde: sel(), hasta: sel().r2 }; e.preventDefault(); return }
    cur = { r: +td.dataset.r, c: +td.dataset.c }
    if (!e.shiftKey) anc = { ...cur }
    arrastrando = true
    render(); hoja.focus({ preventScroll: true }); e.preventDefault()
  }
  const onOver = e => {
    if (editando) return
    if (!(e.buttons & 1)) { arrastrando = false; if (rellenando) return }
    const td = e.target.closest('td[data-r]')
    if (!td) return
    if (arrastrando) { cur = { r: +td.dataset.r, c: +td.dataset.c }; render() }
    if (rellenando) {
      rellenando.hasta = Math.max(rellenando.desde.r2, +td.dataset.r)
      tabla.querySelectorAll('td.pl-prev').forEach(x => x.classList.remove('pl-prev'))
      for (let r = rellenando.desde.r2 + 1; r <= rellenando.hasta; r++) for (let j = rellenando.desde.c1; j <= rellenando.desde.c2; j++) tabla.querySelector(`td[data-r="${r}"][data-c="${j}"]`)?.classList.add('pl-prev')
    }
  }
  const onUp = () => {
    arrastrando = false
    if (!rellenando) return
    const { desde, hasta } = rellenando
    rellenando = null
    if (hasta > desde.r2) { anc = { r: desde.r1, c: desde.c1 }; cur = { r: hasta, c: desde.c2 }; rellenar(desde.r1 === desde.r2 ? desde.r1 : desde.r2, hasta, rango(desde.c1, desde.c2)) } else render()
  }
  const onClick = e => { const b = e.target.closest('.pl-fbtn'); if (b) { e.stopPropagation(); on.filtro?.(+b.dataset.f, b.getBoundingClientRect()) } }
  const onDbl = e => { if (e.target.closest('td[data-r]')) empezar() }
  const onScroll = () => { if (editando) return; cancelAnimationFrame(raf); raf = requestAnimationFrame(render) }

  hoja.addEventListener('keydown', onKey)
  hoja.addEventListener('scroll', onScroll)
  tabla.addEventListener('mousedown', onDown)
  tabla.addEventListener('mouseover', onOver)
  tabla.addEventListener('click', onClick)
  tabla.addEventListener('dblclick', onDbl)
  document.addEventListener('mouseup', onUp)
  document.addEventListener('paste', onPaste)

  return {
    /** Carga filas nuevas (descarta lo no guardado: la pantalla avisa antes). */
    setFilas(nuevas) { filas = nuevas.map(r => ({ ...r })); orig = nuevas.map(r => ({ ...r })); pila.length = 0; cur = { r: 0, c: cur.c }; anc = { ...cur }; hoja.scrollTop = 0; recalcVista(); render() },
    ordenar(k, dir) { orden = k ? { k, dir } : null; recalcVista(); render() },
    orden: () => orden,
    deshacer: undo,
    descartar() { filas = orig.map(r => ({ ...r })); pila.length = 0; render() },
    /** Cambios pendientes: [{ fila, k, antes, ahora }]. */
    cambios() { const out = []; filas.forEach((f, i) => cols.forEach(c => { if (esDirty(i, c.k)) out.push({ fila: f, k: c.k, antes: orig[i][c.k], ahora: f[c.k] }) })); return out },
    /** Lo guardado pasa a ser el nuevo original. */
    confirmar() { orig = filas.map(r => ({ ...r })); pila.length = 0; render() },
    render,
    destruir() {
      document.removeEventListener('mouseup', onUp); document.removeEventListener('paste', onPaste)
      hoja.removeEventListener('keydown', onKey); hoja.removeEventListener('scroll', onScroll)
      tabla.remove()
    },
  }
}

/** Estilos de la grilla (necesitan CSS real: sticky, hover, selectores). */
export const CSS_PLANILLA = `
.pl-tabla { border-collapse: separate; border-spacing: 0; table-layout: fixed; width: max-content; font-size: 13px; }
.pl-tabla th, .pl-tabla td { border-right: 1px solid #dfe4ea; border-bottom: 1px solid #dfe4ea; padding: 0 6px; height: 24px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; box-sizing: border-box; }
.pl-tabla thead th { position: sticky; background: #eef2f7; z-index: 3; font-weight: 600; font-size: 12px; color: #0f172a; user-select: none; }
.pl-tabla thead tr.pl-letras th { top: 0; height: 20px; font-weight: 500; font-size: 10.5px; color: #94a3b8; text-align: center; }
.pl-tabla thead tr.pl-nombres th { top: 20px; text-align: left; }
.pl-h { display: flex; align-items: center; gap: 4px; }
.pl-h span { overflow: hidden; text-overflow: ellipsis; }
.pl-tabla thead th.pl-ro .pl-h span::before { content: "🔒 "; font-size: 9px; opacity: .6; }
.pl-tabla thead th.pl-ed { color: #1d4ed8; }
.pl-fbtn { margin-left: auto; border: 1px solid #c5ccd6; background: #fff; color: #64748b; border-radius: 3px; font-size: 9px; width: 16px; height: 16px; line-height: 14px; padding: 0; cursor: pointer; flex: none; }
.pl-fbtn.on { background: #1d4ed8; color: #fff; border-color: #1d4ed8; }
.pl-resize { position: absolute; right: -3px; top: 0; bottom: 0; width: 7px; cursor: col-resize; z-index: 5; }
.pl-tabla .pl-num { text-align: right; font-family: ui-monospace, "SF Mono", Menlo, monospace; font-variant-numeric: tabular-nums; }
.pl-tabla .pl-rn { position: sticky; left: 0; background: #eef2f7; color: #94a3b8; text-align: center; font-size: 11px; z-index: 2; padding: 0; }
.pl-tabla thead th.pl-rn { z-index: 6; }
.pl-tabla .pl-fz { position: sticky; z-index: 1; }
.pl-tabla thead th.pl-fz { z-index: 5; }
.pl-tabla td { background: #fff; cursor: cell; color: #0f172a; }
.pl-tabla td.pl-ro { background: #f4f6f9; color: #64748b; }
.pl-tabla td.pl-fz { background: #fff; }
.pl-tabla td.pl-mono { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 12px; }
.pl-tabla td.pl-dirty { background: #fff7d6 !important; box-shadow: inset 3px 0 0 #d97706; }
.pl-tabla td.pl-sel { background-image: linear-gradient(rgba(29, 78, 216, .10), rgba(29, 78, 216, .10)); }
.pl-tabla td.pl-cur { outline: 2px solid #1d4ed8; outline-offset: -2px; }
.pl-tabla td.pl-cur:not(.pl-fz) { position: relative; }
.pl-tabla td.pl-vacio { color: #94a3b8; }
.pl-tabla td.pl-prev { box-shadow: inset 0 0 0 1px #1d4ed8; }
.pl-tabla tr.pl-esp td { border: 0; padding: 0; background: transparent; }
.pl-chip { display: inline-block; padding: 0 7px; border-radius: 999px; font-size: 11px; font-weight: 600; line-height: 17px; }
.pl-chip.si { background: #dcfce7; color: #166534; }
.pl-chip.no { background: #fee2e2; color: #991b1b; }
.pl-handle { position: absolute; right: -3px; bottom: -3px; width: 7px; height: 7px; background: #1d4ed8; border: 1px solid #fff; cursor: crosshair; z-index: 4; }
.pl-input { position: absolute; inset: 0; width: 100%; height: 100%; box-sizing: border-box; border: 0; outline: 2px solid #1d4ed8; outline-offset: -2px; font: inherit; padding: 0 5px; background: #fff; color: #0f172a; }
`
